import { useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { SITE_ID, SITE_SHORT_NAME } from '@/config/site';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

/**
 * Dismissible "Add to Home Screen" banner shown on the public site.
 *
 * Two render paths (see useInstallPrompt):
 *   - Android/Chrome/desktop → an Install button that replays the captured
 *     `beforeinstallprompt`.
 *   - iOS Safari → manual Share → "Add to Home Screen" instructions (iOS has
 *     no programmatic install).
 *
 * Hidden once the app is installed — including from a plain browser tab, where
 * `display-mode: standalone` is false and only useInstallPrompt's `isInstalled`
 * knows better — or after the user dismisses it. Dismissals persist to
 * localStorage (falling back to sessionStorage, then module memory, when
 * private browsing blocks writes): permanently after the native install dialog
 * was answered, and as an IOS_RESHOW_AFTER_DAYS snooze for the iOS X button,
 * since iOS re-offers on every visit otherwise. Mounted once in PublicLayout,
 * so it only appears on the host site's own pages — never inside an embedded
 * app iframe or the admin shell.
 */

function dismissKey(): string {
  return `rumio:pwa-install-dismissed:${SITE_ID}`;
}

/**
 * iOS has no `beforeinstallprompt` to gate the banner, so it would otherwise
 * come back on every visit. An X-tap there means "not now" — snooze it and
 * re-offer after this many days. Everywhere else a dismissal is permanent.
 */
const IOS_RESHOW_AFTER_DAYS = 14;
const SNOOZE_MS = IOS_RESHOW_AFTER_DAYS * 24 * 60 * 60 * 1000;

/** Permanent dismissal marker — also the only value the legacy format wrote. */
const DISMISSED_FOREVER = '1';

/**
 * Last-ditch dismissal memory for when BOTH storages are blocked (fully
 * locked-down private browsing). Module-level so the dismissal survives
 * PublicLayout remounts within this page's lifetime — a reload starts fresh,
 * which is the best we can do without any storage.
 */
let memoryDismissed = false;

/** Test-only: clear the in-memory dismissal fallback between tests. */
export function resetDismissMemoryForTests(): void {
  memoryDismissed = false;
}

/**
 * Stored-value grammar (backward compatible):
 *   '1'          → dismissed permanently (the legacy format, and the
 *                  Install-button path where the user answered the native dialog)
 *   '<epoch ms>' → snoozed; re-show once SNOOZE_MS has elapsed (iOS X button)
 * Anything else is an unknown future format — err on the side of not nagging.
 */
function isDismissedValue(value: string | null): boolean {
  if (value === null) return false;
  if (value === DISMISSED_FOREVER) return true;
  const snoozedAt = Number(value);
  if (Number.isFinite(snoozedAt)) return Date.now() - snoozedAt < SNOOZE_MS;
  return true;
}

function readDismissed(): boolean {
  if (memoryDismissed) return true;
  let value: string | null = null;
  try {
    value = localStorage.getItem(dismissKey());
  } catch {
    /* storage blocked */
  }
  if (value === null) {
    // A blocked localStorage write may have landed in sessionStorage instead.
    try {
      value = sessionStorage.getItem(dismissKey());
    } catch {
      /* storage blocked */
    }
  }
  return isDismissedValue(value);
}

function writeDismissed(value: string): void {
  // Set the in-memory flag first: even if every storage write below throws
  // (Safari's lockdown private mode), the dismissal holds for this page.
  memoryDismissed = true;
  try {
    localStorage.setItem(dismissKey(), value);
    return;
  } catch {
    /* quota / privacy mode — fall back to session scope */
  }
  try {
    sessionStorage.setItem(dismissKey(), value);
  } catch {
    /* both blocked — memoryDismissed carries the session */
  }
}

export default function InstallPrompt() {
  const { canInstall, isIOS, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  if (dismissed || (!canInstall && !isIOS)) return null;

  const dismiss = () => {
    // On iOS the X is a snooze (see IOS_RESHOW_AFTER_DAYS); everywhere else
    // it keeps the pre-existing permanent semantics.
    writeDismissed(isIOS ? String(Date.now()) : DISMISSED_FOREVER);
    setDismissed(true);
  };

  const handleInstall = async () => {
    await promptInstall();
    // Whatever the user chose in the native dialog, don't re-prompt — an
    // accept fires `appinstalled` (hides via standalone) and a decline means
    // they said no. Either way, respect it permanently.
    writeDismissed(DISMISSED_FOREVER);
    setDismissed(true);
  };

  // The extra right padding below `lg` keeps the card clear of the corner the
  // AXISS chat widget occupies (a fixed bubble at right: 20px, 56px wide, with
  // a proactive toast above it — third-party styles at z-index ~1e6 that we
  // can't change and always win the stacking battle). Without the reserve, at
  // phone widths the card spans nearly the full row and its dismiss X lands
  // directly UNDER the bubble, which eats every tap — the banner becomes
  // un-dismissable (wawmfd #280). 5.5rem = 20px offset + 56px bubble + 12px
  // gap. At lg+ the centered max-w-2xl card clears the corner by ≥176px on
  // its own.
  return (
    <div
      role="dialog"
      aria-label={`Install ${SITE_SHORT_NAME}`}
      className="fixed inset-x-0 bottom-0 z-[900] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pr-[5.5rem] sm:p-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))] sm:pr-[5.5rem] lg:pr-4"
    >
      {/* Brand fills here use --color-fixed-sage-*, NOT --color-sage-*. The sage
          ramp INVERTS under .dark (apply-theme writes generated.dark to
          :root.dark), so sage-700 resolves to a pale tint while `text-white`
          stays white: measured 2.51:1 on aircleared.com, below WCAG AA. The
          fixed alias is never overridden in dark, so white-on-brand holds in
          both modes — the same trick the footer band and header strip use. */}
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-2xl border border-line bg-surface p-3 shadow-2xl sm:p-4">
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-fixed-sage-700,#444f38)] text-white">
          <Download className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          {isIOS ? (
            <p className="text-sm text-ink-soft">
              Install <span className="font-semibold">{SITE_SHORT_NAME}</span>: tap the
              {' '}
              <Share className="-mt-0.5 inline h-4 w-4" aria-label="Share" />
              {' '}
              Share button, then <span className="font-semibold">Add to Home Screen</span>.
            </p>
          ) : (
            <p className="text-sm text-ink-soft">
              Install <span className="font-semibold">{SITE_SHORT_NAME}</span> for a faster,
              full-screen experience.
            </p>
          )}
        </div>

        {canInstall && !isIOS && (
          <button
            type="button"
            onClick={handleInstall}
            className="shrink-0 rounded-full bg-[var(--color-fixed-sage-700,#444f38)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Install
          </button>
        )}

        {/* h-11/w-11 = 44px, the iOS HIG minimum tap target — the old 36px
            X was too easy to miss on the phones this banner targets. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink-soft"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
